import { useNavigate } from 'react-router-dom';
import { CreditCard, ExternalLink, Calendar, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useSubscription } from '@/hooks/use-subscription';
import { useEntitlements } from '@/hooks/use-entitlements';
import { useManageSubscription } from '@/hooks/use-manage-subscription';

export default function Billing() {
  const navigate = useNavigate();
  const { subscription, isSubscribed, isCanceling, planName, currentPeriodEnd } = useSubscription();
  const { entitlements, isPaid, isFree, gracePeriodActive } = useEntitlements();
  const { openPortal, loading: portalLoading } = useManageSubscription();

  const formattedPeriodEnd = currentPeriodEnd
    ? new Date(currentPeriodEnd).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const formattedGraceEnd = entitlements.grace_period_ends_at
    ? new Date(entitlements.grace_period_ends_at).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-2">Billing & Plan</h1>
        <p className="text-muted-foreground mb-8">Manage your subscription and billing details.</p>

        {/* Current Plan */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Current Plan
            </CardTitle>
            <CardDescription>
              {isSubscribed
                ? planName || 'Paid Plan'
                : isFree
                  ? 'Free'
                  : 'No active plan'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isSubscribed && (
              <div className="space-y-3">
                {formattedPeriodEnd && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {isCanceling
                        ? `Access until ${formattedPeriodEnd}`
                        : `Renews on ${formattedPeriodEnd}`}
                    </span>
                  </div>
                )}
                {isCanceling && (
                  <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="h-4 w-4" />
                    <span>Your subscription will cancel at the end of the billing period.</span>
                  </div>
                )}
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={openPortal} disabled={portalLoading}>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {portalLoading ? 'Opening...' : 'Manage Subscription'}
                  </Button>
                </div>
              </div>
            )}

            {isFree && (
              <div className="space-y-3">
                {gracePeriodActive && formattedGraceEnd && (
                  <p className="text-sm text-muted-foreground">
                    Unlimited access until {formattedGraceEnd}. After that, daily limits apply.
                  </p>
                )}
                {!gracePeriodActive && (
                  <p className="text-sm text-muted-foreground">
                    {entitlements.daily_reports_remaining !== null
                      ? `${entitlements.daily_reports_remaining} of ${entitlements.reports_limit} reports remaining today.`
                      : 'Unlimited reports.'}
                  </p>
                )}
                <Button onClick={() => navigate('/app/billing/plans')}>
                  Upgrade to Pro
                </Button>
              </div>
            )}

            {!isSubscribed && !isFree && (
              <div>
                <p className="text-sm text-muted-foreground mb-3">
                  You don't have an active subscription.
                </p>
                <Button onClick={() => navigate('/app/billing/plans')}>
                  View Plans
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Usage Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Usage</CardTitle>
            <CardDescription>Your current reporting usage.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Reports Today</p>
                <p className="text-2xl font-bold">{entitlements.daily_reports_used}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Remaining</p>
                <p className="text-2xl font-bold">
                  {entitlements.daily_reports_remaining === null ? 'Unlimited' : entitlements.daily_reports_remaining}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
